import React from 'react';
import * as S from './MobileLogSearchBar.styled';
import Search from '@mui/icons-material/Search';
import Block from '@mui/icons-material/Block';
import Close from '@mui/icons-material/Close';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import Flight from '@mui/icons-material/Flight';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  LogSearchOptions,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  setLogSearch,
  setLogSearches,
} from '../redux';
import { hasActiveLogSearch } from '../logSearch';
import { moveTmpPanel } from 'pages/Navigation/utils';

type MobileLogSearchBarProps = {
  /** CSS max-width breakpoint string, e.g. `925px` — bar only shows at/below this. */
  breakpoint: string,
  /** Optional lower bound so the bar does not double with the app-header strip. */
  minBreakpoint?: string,
  /** Render above the mobile app header (not sticky inside page scroll). */
  aboveHeader?: boolean,
  /** Open audits selector panel on chip tap. */
  openAuditsPanel?: boolean,
};

const chipIcon = (options: LogSearchOptions) => {
  if (options.matchMode === 'room') return <MeetingRoomOutlined fontSize="inherit" />;
  if (options.matchMode === 'staff') return <PersonOutline fontSize="inherit" />;
  if (options.matchMode === 'customer') return <PeopleOutline fontSize="inherit" />;
  if (options.matchMode === 'department') return <AccountTreeOutlined fontSize="inherit" />;
  if (options.matchMode === 'ota') return <Flight fontSize="inherit" />;
  if (options.fromFullLogs) return <LibraryAdd fontSize="inherit" />;
  if (options.negate) return <Block fontSize="inherit" />;
  return <Search fontSize="inherit" />;
};

/**
 * Compact committed (+ draft) search criteria for mobile layouts.
 * Used above the app header and above mobile audits/metrics selector headers.
 */
const MobileLogSearchBar = ({
  breakpoint,
  minBreakpoint,
  aboveHeader,
  openAuditsPanel = true,
}: MobileLogSearchBarProps) => {
  const dispatch = useAppDispatch();
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);

  if (!hasActiveLogSearch(logSearches, logSearch)) return null;

  const draft = logSearch.trim();
  const chips = [
    ...logSearches.map((f, i) => ({ ...f, key: `chip_${i}`, index: i as number | null })),
    ...(draft
      ? [{
        term: draft,
        options: logSearchOptions,
        key: 'draft',
        index: null as number | null,
      }]
      : []),
  ];

  const removeChip = (index: number | null) => {
    if (index == null) {
      dispatch(setLogSearch(''));
      return;
    }
    dispatch(setLogSearches(logSearches.filter((_, i) => i !== index)));
  };

  return (
    <S.Bar
      $breakpoint={breakpoint}
      $minBreakpoint={minBreakpoint}
      $aboveHeader={aboveHeader}
    >
      <S.Chips>
        {chips.map((chip) => (
          <S.Chip
            key={chip.key}
            $negate={chip.options.negate}
            type="button"
            onClick={() => {
              if (openAuditsPanel) moveTmpPanel('tmp_panel0');
            }}
            title={chip.options.negate ? `Exclude: ${chip.term}` : chip.term}
          >
            <S.ChipIcon>
              {chipIcon(chip.options)}
            </S.ChipIcon>
            <S.ChipText>{chip.term}</S.ChipText>
            <S.ChipRemove
              type="button"
              aria-label={`Remove search “${chip.term}”`}
              onClick={(e) => {
                e.stopPropagation();
                removeChip(chip.index);
              }}
            >
              <Close fontSize="inherit" />
            </S.ChipRemove>
          </S.Chip>
        ))}
      </S.Chips>
    </S.Bar>
  );
};

export default MobileLogSearchBar;
